import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Heading } from '@/components/Heading';
import { InstallPrompt } from '@/components/InstallPrompt';
import { ModificationsSection } from '@/components/ModificationsSection';
import { Pill } from '@/components/Pill';
import { PreviousOwnersList } from '@/components/PreviousOwnersList';
import { SkeletonList } from '@/components/Skeleton';
import { SpecBlock, SpecRows, type SpecRow } from '@/components/SpecBlock';
import { TransferPendingBadge } from '@/components/TransferPendingBadge';
import { F } from '@/design/fonts';
import { SPACE, T } from '@/design/tokens';
import { useAuth } from '@/features/auth/useAuth';
import { PhotoAlbumSection } from '@/features/photos/PhotoAlbumSection';
import { AttestModal } from '@/features/trust/AttestModal';
import { ProvenancePanel } from '@/features/trust/ProvenancePanel';
import { RegisterAttesterModal } from '@/features/trust/RegisterAttesterModal';
import { ExportPanel } from '@/features/vehicles/ExportPanel';
import { usePhotos } from '@/hooks/usePhotos';
import { useProofBundle } from '@/hooks/useProofBundle';
import { useTransferStatus } from '@/hooks/useTransferStatus';
import { useAttestLedgerHead, useRegisterAttester } from '@/hooks/useTrustActions';
import { useUserVehicles } from '@/hooks/useUserVehicles';
import { useVehicle } from '@/hooks/useVehicle';
import { env } from '@/lib/env';
import {
  provenanceOverallStatus,
  type ProvenanceOverallStatus,
} from '@/lib/trust/provenanceStatus';
import {
  ArrowRightLeft,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldQuestion,
  ShieldX,
} from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

export function VehicleDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const { data: vehicle, loading, error, fromCache, hasPendingWrites } = useVehicle(id);
  const { transfer, isPending } = useTransferStatus(env.VITE_TRANSFER_ENABLED ? id : null);
  const { data: photos, isLoading: photosLoading } = usePhotos(env.VITE_PHOTOS_ENABLED ? id : null);

  // D5: whether this is the user's first-ever vehicle — gates the richer
  // "first vehicle sealed" milestone treatment (vs. the plain repeat-add
  // banner) and arms the install-prompt bottom bar. See useUserVehicles for
  // the underlying real-time Firestore listener.
  const { user } = useAuth();
  const { data: allVehicles, loading: allVehiclesLoading } = useUserVehicles(user?.uid);

  // Trust layer — proof bundle + register/attest mutations.
  const { bundle, loading: bundleLoading, error: bundleError } = useProofBundle(id || null);
  const registerMutation = useRegisterAttester();
  const attestMutation = useAttestLedgerHead();

  // Stable id for aria-controls linking the chip button → provenance body.
  const provenanceBodyId = useId();

  // Trust chip state — derived via provenanceOverallStatus from the panel's
  // onVerifyResult callback. Starts as 'checking' while the bundle loads.
  const [trustVerifyState, setTrustVerifyState] = useState<{
    verifying: boolean;
    integrityStatus: 'ok' | 'fail' | 'none';
    sealStatus: 'ok' | 'fail' | 'none';
    attestationVerifyStatus: 'ok' | 'fail' | 'none';
    verifyError: string | null;
    /** headHash of the bundle the last verdict was computed for. */
    verifiedHead: string | null;
  }>({
    verifying: false,
    integrityStatus: 'none',
    sealStatus: 'none',
    attestationVerifyStatus: 'none',
    verifyError: null,
    verifiedHead: null,
  });

  // Stable callback — MUST NOT be recreated each render. ProvenancePanel's
  // verify effect notifies through this; an unstable identity would re-trigger
  // the effect → re-verify → setState → re-render → infinite loop (heap OOM).
  const handleVerifyResult = useCallback(
    (r: {
      verifying: boolean;
      integrityStatus: 'ok' | 'fail' | 'none';
      sealStatus: 'ok' | 'fail' | 'none';
      attestationVerifyStatus: 'ok' | 'fail' | 'none';
      verifyError: string | null;
      verifiedHead: string | null;
    }) => setTrustVerifyState(r),
    [],
  );

  // Bundle-identity guard: if the current bundle's headHash doesn't match the
  // head that was verified, we have a new bundle that hasn't been verified yet.
  // Treat it as verifying so provenanceOverallStatus returns 'checking' rather
  // than re-using a stale green/red verdict. This closes the race window where
  // a background TanStack Query refetch swaps the bundle while the panel's
  // verify-effect hasn't fired onVerifyResult({verifying:true}) yet.
  const bundleMatchesVerified =
    bundle === null || bundle.headHash === trustVerifyState.verifiedHead;

  // Map wire disputeStatus ('underReview') to internal disputeState ('under-review').
  // The vehicle doc is read cache-first via useVehicle (Firestore offline-persistent).
  // gg-web never writes this field — it is server-controlled.
  const disputeState: 'none' | 'under-review' | 'contested' =
    vehicle?.disputeStatus === 'underReview'
      ? 'under-review'
      : vehicle?.disputeStatus === 'contested'
        ? 'contested'
        : 'none';

  const overallTrustStatus: ProvenanceOverallStatus = provenanceOverallStatus({
    loading: bundleLoading,
    error: bundleError,
    hasBundle: !!bundle,
    // If the bundle identity changed, force verifying=true so the helper
    // returns 'checking' regardless of the stale status values in state.
    verifying: trustVerifyState.verifying || !bundleMatchesVerified,
    integrityStatus: bundleMatchesVerified ? trustVerifyState.integrityStatus : 'none',
    sealStatus: bundleMatchesVerified ? trustVerifyState.sealStatus : 'none',
    attestationVerifyStatus: bundleMatchesVerified
      ? trustVerifyState.attestationVerifyStatus
      : 'none',
    verifyError: bundleMatchesVerified ? trustVerifyState.verifyError : null,
    disputeState,
  });

  // Provenance panel open/close. Force-opens when status is 'failed'.
  const [provenancePanelOpen, setProvenancePanelOpen] = useState(false);

  // When overall status becomes 'failed' or 'contested', force-open the panel.
  // 'under-review' does NOT force-open — it is informational, not alarming.
  useEffect(() => {
    if (overallTrustStatus === 'failed' || overallTrustStatus === 'contested') {
      setProvenancePanelOpen(true);
    }
  }, [overallTrustStatus]);

  // Modal state.
  const [registerOpen, setRegisterOpen] = useState(false);
  const [attestOpen, setAttestOpen] = useState(false);
  // credId from a registration completed in this session. Without a
  // /v1/attesters/me endpoint this is how the attest step learns which
  // credential to assert with after the user just registered.
  const [registeredCredId, setRegisteredCredId] = useState<string | null>(null);
  // Statement preserved across a register-first detour: when the user clicks
  // Attest without a registered credential, we redirect to the register modal
  // and stash the statement here. After register succeeds we auto-fire the
  // attest mutation with this statement + the new credId so the perceived
  // flow is one click ("Attest") + two passkey taps (create + assert), not
  // "Attest, get redirected, register, then click Attest again".
  const [pendingAttestStatement, setPendingAttestStatement] = useState<string | null>(null);

  function handleRegister() {
    registerMutation.reset();
    setRegisterOpen(true);
  }

  function handleRegisterConfirm(label: string) {
    registerMutation.mutate(
      { label },
      {
        onSuccess: (identity) => {
          setRegisteredCredId(identity.credId);
          setRegisterOpen(false);
          // If register was the second leg of an attest-first flow, reopen
          // the attest modal with the statement pre-filled. We CANNOT call
          // attestMutation.mutate here directly: iOS Safari (and other
          // strict WebAuthn UAs) require a fresh user gesture for every
          // navigator.credentials.get() call, and the activation token has
          // already been consumed by the create() ceremony. The user must
          // tap "Confirm with passkey" again to provide a new gesture — at
          // which point the assertion ceremony succeeds.
          if (pendingAttestStatement !== null) {
            attestMutation.reset();
            setAttestOpen(true);
          }
        },
        // Error stays visible in the modal.
      },
    );
  }

  function handleAttest() {
    attestMutation.reset();
    setAttestOpen(true);
  }

  function handleAttestConfirm(statement: string) {
    if (!bundle) return;
    // Prefer the credId from a registration done in this session, then fall
    // back to the most-recent attestation's credId. If neither exists the
    // user has not registered yet — send them to register first AND stash
    // the statement so handleRegisterConfirm can auto-fire attest on success.
    // A future /v1/attesters/me endpoint will make this automatic across sessions.
    const credId = registeredCredId ?? bundle.attestation?.attesterCredId ?? '';
    if (!credId) {
      attestMutation.reset();
      setAttestOpen(false);
      setPendingAttestStatement(statement);
      setRegisterOpen(true);
      return;
    }
    attestMutation.mutate(
      { vehicleId: id, statement, credId },
      {
        onSuccess: () => {
          setAttestOpen(false);
          setPendingAttestStatement(null);
        },
      },
    );
  }

  // Post-navigate welcome banners. Both are cleared via replace-navigation so
  // refresh doesn't re-show them.
  // - justTransferred: spec 007 — accepted a transfer from another owner.
  // - justCreated: genesis flow — freshly created their first (or any) vehicle.
  const location = useLocation();
  const navigate = useNavigate();
  type LocationState = { justTransferred?: boolean; justCreated?: boolean } | null;
  const locationState = location.state as LocationState;
  const justTransferred = locationState?.justTransferred;
  const justCreated = locationState?.justCreated;
  const [showWelcome, setShowWelcome] = useState<boolean>(!!justTransferred);
  // G12: show a "vehicle added" welcome for freshly-created vehicles.
  const [showJustCreated, setShowJustCreated] = useState<boolean>(!!justCreated);
  useEffect(() => {
    if (justTransferred || justCreated) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [justTransferred, justCreated, location.pathname, navigate]);

  // D5: the very first vehicle a user ever adds gets the richer "sealed"
  // milestone treatment instead of the plain repeat-add banner. While the
  // vehicles listener is still loading we deliberately fall back to the
  // plain copy (isFirstEverVehicle stays false) rather than flash the
  // milestone in after a beat — it upgrades in place once resolved, which
  // is fast since this listener was already primed by the genesis write.
  const isFirstEverVehicle = !allVehiclesLoading && allVehicles.length === 1;

  if (loading) {
    return <SkeletonList count={3} />;
  }
  if (error || !vehicle) {
    return (
      <Card accent="red">
        <p style={{ margin: 0, color: T.red }}>{error?.message ?? 'Vehicle not found.'}</p>
        <BackLink to="/vehicles">← garage</BackLink>
      </Card>
    );
  }

  return (
    <section>
      <BackLink to="/vehicles">← garage</BackLink>
      {/* Dossier spec-block — chrome matches the service-log ledger table */}
      <SpecBlock style={{ marginTop: SPACE.sm }}>
        {/* Title zone */}
        <div
          style={{
            padding: SPACE.md,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: SPACE.sm,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Heading level={1} size="xxl">
              {vehicle.nickname ?? `${vehicle.year} ${vehicle.make}`}
            </Heading>
            <p
              style={{
                color: T.textMuted,
                marginTop: SPACE.xs,
                fontFamily: F.body,
                margin: `${SPACE.xs}px 0 0`,
              }}
            >
              {vehicle.year} {vehicle.make} {vehicle.model}
            </p>
          </div>
          <div style={{ display: 'flex', gap: SPACE.xs, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Trust chip — always visible, toggles provenance panel */}
            <TrustChip
              status={overallTrustStatus}
              panelOpen={provenancePanelOpen}
              panelBodyId={provenanceBodyId}
              onToggle={() => setProvenancePanelOpen((o) => !o)}
            />
            {fromCache ? <Pill color="orange">Cached</Pill> : null}
            {hasPendingWrites ? <Pill color="orange">Syncing</Pill> : null}
          </div>
        </div>

        {/* Ruled identifier rows — only rendered when at least one field is present.
            Rows in canonical order: CHASSIS (required), ENGINE (optional), VIN (optional).
            Absent fields produce no row/rule. Last row carries no border-bottom. */}
        {(() => {
          const rows: SpecRow[] = [];
          if (vehicle.chassisCode) rows.push({ label: 'Chassis', value: vehicle.chassisCode });
          if (vehicle.engineCode) rows.push({ label: 'Engine', value: vehicle.engineCode });
          if (vehicle.vin)
            rows.push({ label: 'VIN', value: vehicle.vin, valueStyle: { wordBreak: 'break-all' } });
          return <SpecRows rows={rows} />;
        })()}
      </SpecBlock>

      {/* G12 / D5: justCreated welcome — shown when navigating here from
          VehicleGenesisPage. The very first vehicle gets a dignified-milestone
          treatment (seal icon, two lines, gentle next-step nudge) — everything
          after that keeps the plain one-liner. Deliberately restrained: no
          confetti, no celebration chrome, just a genuine acknowledgment. */}
      {showJustCreated ? (
        <Card accent="green" style={{ marginTop: SPACE.md }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: SPACE.sm }}>
            {isFirstEverVehicle ? (
              <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'flex-start' }}>
                <ShieldCheck
                  size={20}
                  color={T.green}
                  aria-hidden="true"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <div>
                  <p style={{ color: T.text, margin: 0, fontSize: 14, fontWeight: 700 }}>
                    Your first vehicle is sealed.
                  </p>
                  <p
                    style={{
                      color: T.text,
                      margin: `${SPACE.xs}px 0 0`,
                      fontSize: 14,
                      lineHeight: 1.6,
                    }}
                  >
                    This record now has a cryptographic seal — a tamper-evident history that travels
                    with the car for as long as you keep it. Every service entry, photo, and future
                    owner adds to the same sealed timeline.
                  </p>
                  <Link
                    to={`/vehicles/${id}/service`}
                    style={{ fontSize: 13, fontWeight: 600, color: T.accent }}
                  >
                    Start the service log →
                  </Link>
                </div>
              </div>
            ) : (
              <p style={{ color: T.text, margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                Vehicle added. Your record is building. The provenance seal will appear once the
                genesis entry is confirmed.
              </p>
            )}
            <button
              type="button"
              onClick={() => setShowJustCreated(false)}
              aria-label="Dismiss vehicle added message"
              style={{
                background: 'transparent',
                border: 'none',
                color: T.textMuted,
                fontSize: 18,
                cursor: 'pointer',
                marginLeft: SPACE.sm,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
        </Card>
      ) : null}

      {/* Install-prompt bottom bar — armed only by this exact first-vehicle
          moment (never on cold launch). See InstallPrompt / useInstallPrompt
          for the capability check (beforeinstallprompt capture, iOS fallback). */}
      <InstallPrompt armed={showJustCreated && isFirstEverVehicle} />

      {showWelcome ? (
        <Card accent="green" style={{ marginTop: SPACE.md }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <p style={{ color: T.text, margin: 0, fontSize: 14, lineHeight: 1.6 }}>
              Welcome. This vehicle is now in your Garage. The previous owner's service history
              travels with it.
            </p>
            <button
              type="button"
              onClick={() => setShowWelcome(false)}
              aria-label="Dismiss welcome message"
              style={{
                background: 'transparent',
                border: 'none',
                color: T.textMuted,
                fontSize: 18,
                cursor: 'pointer',
                marginLeft: SPACE.sm,
              }}
            >
              ×
            </button>
          </div>
        </Card>
      ) : null}

      {env.VITE_TRANSFER_ENABLED && isPending && transfer ? (
        <TransferPendingBadge
          transferId={transfer.id}
          code={transfer.code}
          expiresAt={transfer.expiresAt}
        />
      ) : null}

      <div style={{ marginTop: SPACE.lg }}>
        <PreviousOwnersList previousOwners={vehicle.previous_owners} />
      </div>

      <div style={{ marginTop: SPACE.lg }}>
        <ProvenancePanel
          vehicleId={id}
          {...(bundle?.headHash !== undefined ? { expectedHead: bundle.headHash } : {})}
          bundle={bundle}
          loading={bundleLoading}
          error={bundleError}
          onRegister={handleRegister}
          onAttest={handleAttest}
          defaultOpen={provenancePanelOpen}
          bodyId={provenanceBodyId}
          onOpenChange={setProvenancePanelOpen}
          onVerifyResult={handleVerifyResult}
          disputeState={disputeState}
        />
      </div>

      <RegisterAttesterModal
        isOpen={registerOpen}
        error={registerMutation.error?.message ?? null}
        isPending={registerMutation.isPending}
        onConfirm={handleRegisterConfirm}
        onCancel={() => setRegisterOpen(false)}
      />

      <AttestModal
        isOpen={attestOpen}
        error={attestMutation.error?.message ?? null}
        isPending={attestMutation.isPending}
        defaultStatement={pendingAttestStatement ?? undefined}
        onConfirm={handleAttestConfirm}
        onCancel={() => {
          setAttestOpen(false);
          setPendingAttestStatement(null);
        }}
      />

      {/* Modifications & Concours Impact — inline dossier section (E5).
          ModificationsSection provides its own Collapsible card; the SpecBlock
          wrapper was removed to avoid double-bordering. */}
      <div style={{ marginTop: SPACE.lg }}>
        <ModificationsSection vehicleId={id} />
      </div>

      {/* Photo album — vehicle photos. Gated by VITE_PHOTOS_ENABLED. */}
      {env.VITE_PHOTOS_ENABLED ? (
        <PhotoAlbumSection vehicleId={id} photos={photos} loading={photosLoading} />
      ) : null}

      {/* Hairline table-of-contents — demoted below dossier content.
          One ruled list, no per-row boxes; mirror ledger 4%-pink row hover.
          Transfer is intentionally absent from this nav — it lives in the
          weighted action zone below, signalling its different class of action. */}
      <nav style={{ borderTop: `1px solid ${T.border}`, marginTop: SPACE.lg }}>
        <NavRow to={`/vehicles/${vehicle.id}/service`} label="Service log" />
        {/* AI deferred — see plan §03/Phase 6. The /v1/diagnose endpoint is not
            deployed in the current gg-app build; surfacing the link causes dead
            UX. Re-enable once the AI feature ships. */}
        <NavRow to={`/vehicles/${vehicle.id}/projects`} label="Projects & tasks" />
      </nav>

      {/* Transfer zone — weighted action, below the nav.
          Rendered only when the feature is enabled and no transfer is pending.
          (TransferPendingBadge near the top already shows the in-flight state.) */}
      {env.VITE_TRANSFER_ENABLED && !isPending ? <TransferZone vehicleId={vehicle.id} /> : null}

      {/* Export zone — spec 010. Rendered only when the feature flag is on. */}
      {env.VITE_EXPORT_ENABLED ? <ExportPanel vehicleId={vehicle.id} /> : null}
    </section>
  );
}

function NavRow({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: SPACE.sm,
        padding: `${SPACE.md}px 0`,
        borderBottom: `1px solid ${T.border}`,
        minHeight: 44,
        textDecoration: 'none',
        color: 'inherit',
      }}
      onMouseEnter={(ev) => {
        (ev.currentTarget as HTMLElement).style.background =
          `color-mix(in srgb, ${T.accent} 4%, transparent)`;
      }}
      onMouseLeave={(ev) => {
        (ev.currentTarget as HTMLElement).style.background = '';
      }}
    >
      <span style={{ fontWeight: 600, color: T.text, fontFamily: F.body }}>{label}</span>
      <span style={{ color: T.textMuted }} aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// TrustChip — always-visible trust status, toggles provenance panel.
// ---------------------------------------------------------------------------

interface TrustChipProps {
  status: ProvenanceOverallStatus;
  panelOpen: boolean;
  panelBodyId: string;
  onToggle: () => void;
}

function TrustChip({ status, panelOpen, panelBodyId, onToggle }: TrustChipProps) {
  const isGreen = status === 'verified' || status === 'verified-attested';
  const isRed = status === 'failed' || status === 'contested';

  const Icon =
    status === 'failed'
      ? ShieldOff
      : status === 'contested'
        ? ShieldX
        : status === 'under-review'
          ? ShieldAlert
          : isGreen
            ? ShieldCheck
            : ShieldQuestion;

  const label =
    status === 'verified-attested'
      ? 'Verified · Attested'
      : status === 'verified'
        ? 'Verified'
        : status === 'failed'
          ? 'Integrity failed'
          : status === 'contested'
            ? 'Identity disputed'
            : status === 'under-review'
              ? 'Under review'
              : status === 'none'
                ? 'No record'
                : status === 'checking'
                  ? 'Checking…'
                  : 'Unavailable';

  // Map trust status to Pill color token or neutral variant.
  const pillColor = isRed ? 'red' : isGreen ? 'green' : undefined;

  return (
    // Outer button provides the ≥44px hit area via transparent padding.
    // The visual Pill is small; the padding adds touch area without
    // altering the visual footprint. The <Pill> renders the visual chip;
    // the button wrapper owns the aria/interaction layer.
    <button
      type="button"
      aria-expanded={panelOpen}
      aria-controls={panelBodyId}
      aria-label={`Trust status: ${label}. ${panelOpen ? 'Collapse' : 'Expand'} provenance panel.`}
      onClick={onToggle}
      style={{
        background: 'transparent',
        border: 'none',
        padding: `${SPACE.xs}px`,
        margin: `-${SPACE.xs}px`,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 44,
        minHeight: 44,
      }}
    >
      {pillColor ? (
        <Pill color={pillColor} size="sm">
          <Icon size={12} aria-hidden="true" />
          {label}
        </Pill>
      ) : (
        <Pill variant="neutral" size="sm">
          <Icon size={12} aria-hidden="true" />
          {label}
        </Pill>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// TransferZone — weighted action below the sub-nav.
// ---------------------------------------------------------------------------

function TransferZone({ vehicleId }: { vehicleId: string }) {
  const captionId = `transfer-caption-${vehicleId}`;
  return (
    <div style={{ marginTop: SPACE.xl }}>
      <Button
        as="a"
        href={`/vehicles/${vehicleId}/transfer`}
        variant="secondary"
        size="md"
        aria-describedby={captionId}
        style={{ borderColor: T.text }}
      >
        <ArrowRightLeft size={14} aria-hidden="true" />
        Transfer this vehicle
      </Button>
      <p
        id={captionId}
        style={{
          marginTop: SPACE.xs,
          fontSize: 12,
          color: T.textMuted,
          fontFamily: F.body,
          lineHeight: 1.5,
        }}
      >
        Hands the car and its full sealed record to the next keeper in one step. Your profile stays
        yours.
      </p>
    </div>
  );
}
